import type { ResultsAiStreamEvent, ResultsAiThreadTurn } from 'definitions';
import type { UserSummary } from 'pertentodb/types';
import { firstNameOf } from 'helpers/user-identity';

/**
 * The results chat's conversation as the panel shows it: the experiment's stored turns (GET
 * /thread, shared by everyone with access) followed by this browser's turns the server does not
 * have yet (one being answered, or one refused before it started). Pure: what a turn is, how each
 * streamed event changes it, and how the two lists meet.
 */

export type TurnError = { message: string; retryable: boolean; status?: number };

export type ResultsTurn = {
  /** Stable: the turn id once known, a local id before that. */
  key: string;
  turnId: string | null;
  question: string;
  /** The answer as it has streamed so far, or as it was stored. */
  answer: string;
  steps: string[];
  phase: 'working' | 'done' | 'stopped' | 'errored';
  /** Live while working, then the charged number (for a stored turn, from its usage row). */
  credits?: number;
  error?: TurnError;
  /** Who asked, for a stored turn whose author is known. */
  sender?: UserSummary;
  /** The figures have changed since this answer read them. */
  stale?: boolean;
};

type NewTurnParams = { key: string; question: string };

export const newTurn = ({ key, question }: NewTurnParams): ResultsTurn => ({
  key,
  turnId: null,
  question,
  answer: '',
  steps: [],
  phase: 'working',
});

/** One streamed event applied to the turn it belongs to. Never mutates: a new turn each time. */
export const applyEvent = (turn: Readonly<ResultsTurn>, event: Readonly<ResultsAiStreamEvent>): ResultsTurn => {
  switch (event.type) {
    case 'turn':
      return { ...turn, turnId: event.turnId };
    case 'status':
      return turn.steps.includes(event.step) ? { ...turn } : { ...turn, steps: [...turn.steps, event.step] };
    case 'text':
      return { ...turn, answer: turn.answer + event.text };
    case 'usage':
      return { ...turn, credits: event.credits };
    case 'error':
      return { ...turn, error: { message: event.message, retryable: event.retryable } };
    case 'done':
      if (event.status === 'aborted') return { ...turn, phase: 'stopped' };
      return { ...turn, phase: event.status === 'errored' || turn.error ? 'errored' : 'done' };
  }
};

/** Thinking until the first words of the answer arrive, writing after. Null when nothing runs. */
export const workingPhase = (turn: Readonly<ResultsTurn> | undefined): 'thinking' | 'writing' | null => {
  if (!turn || turn.phase !== 'working') return null;
  return turn.answer.length > 0 ? 'writing' : 'thinking';
};

const STORED_PHASE: Record<ResultsAiThreadTurn['status'], ResultsTurn['phase']> = {
  completed: 'done',
  errored: 'errored',
  aborted: 'stopped',
};

type FromThreadTurnParams = {
  turn: Readonly<ResultsAiThreadTurn>;
  authors: Readonly<Record<string, UserSummary>>;
};

/**
 * A stored turn as the panel shows it. A completed turn with an error message is a refusal. Try
 * again is offered where the server said asking again could help (`errorRetryable`), so it
 * survives the thread being read again and a reload.
 */
export const fromThreadTurn = ({ turn, authors }: FromThreadTurnParams): ResultsTurn => ({
  key: turn.turnId,
  turnId: turn.turnId,
  question: turn.question,
  answer: turn.answer,
  steps: [],
  phase: turn.errorMessage && turn.status === 'completed' ? 'errored' : STORED_PHASE[turn.status],
  ...(turn.errorMessage ? { error: { message: turn.errorMessage, retryable: turn.errorRetryable } } : {}),
  ...(authors[String(turn.senderId)] ? { sender: authors[String(turn.senderId)] } : {}),
  ...(turn.credits === null ? {} : { credits: turn.credits }),
  stale: turn.stale,
});

/**
 * The stored turns in the server's order (when each was asked, on the server's clock), then this
 * browser's turns the server does not have yet, never one twice. Never sorted across the two: a
 * browser's clock can be minutes off the server's, and this browser's own turn (being answered, or
 * refused before it started) is its latest action, so it stays last, where its answer, the
 * announcer and Try again expect it.
 */
export const mergeTurns = <T extends ResultsTurn>(stored: readonly T[], local: readonly T[]): T[] => {
  const known = new Set(stored.map((turn) => turn.turnId));
  return [...stored, ...local.filter((turn) => !turn.turnId || !known.has(turn.turnId))];
};

/** "Anna", or "A colleague" when the name is not known. */
export const senderName = (user: Readonly<UserSummary> | undefined): string => firstNameOf(user) ?? 'A colleague';
